import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, gql } from "@apollo/client";
import { useAuth } from "../context/AuthContext";
import { useAcknowledgedTerms } from "../hooks/useAcknowledgedTerms";
import SaunaForm, { SaunaFields, emptySaunaFields } from "../components/SaunaForm";
import SaunaImageManager from "../components/SaunaImageManager";
import AvailabilityEditor, { AvailabilityEditorHandle } from "../components/AvailabilityEditor";

const CREATE_SAUNA = gql`
  mutation WizardCreateSauna(
    $name: String!
    $description: String
    $addressLine: String
    $city: String
    $state: String
    $postalCode: String
    $country: String
    $latitude: BigFloat
    $longitude: BigFloat
    $capacity: Int!
    $pricePerHourCents: Int!
    $saunaType: SaunaType
    $amenityIds: [Int!]
    $requiresApproval: Boolean
    $isActive: Boolean
  ) {
    createOwnedSauna(input: {
      name: $name
      description: $description
      addressLine: $addressLine
      city: $city
      state: $state
      postalCode: $postalCode
      country: $country
      latitude: $latitude
      longitude: $longitude
      capacity: $capacity
      pricePerHourCents: $pricePerHourCents
      saunaType: $saunaType
      amenityIds: $amenityIds
      requiresApproval: $requiresApproval
      isActive: $isActive
    }) {
      sauna { id }
    }
  }
`;

const UPDATE_SAUNA = gql`
  mutation WizardUpdateSauna(
    $id: Int!
    $name: String!
    $description: String
    $addressLine: String
    $city: String
    $state: String
    $postalCode: String
    $country: String
    $latitude: BigFloat
    $longitude: BigFloat
    $capacity: Int!
    $pricePerHourCents: Int!
    $saunaType: SaunaType
    $amenityIds: [Int!]
    $requiresApproval: Boolean
  ) {
    updateMySauna(input: {
      id: $id
      name: $name
      description: $description
      addressLine: $addressLine
      city: $city
      state: $state
      postalCode: $postalCode
      country: $country
      latitude: $latitude
      longitude: $longitude
      capacity: $capacity
      pricePerHourCents: $pricePerHourCents
      saunaType: $saunaType
      amenityIds: $amenityIds
      requiresApproval: $requiresApproval
    }) {
      sauna { id }
    }
  }
`;

const SET_ACTIVE = gql`
  mutation WizardSetActive($id: Int!, $isActive: Boolean!) {
    setSaunaActive(input: { id: $id, isActive: $isActive }) {
      sauna { id }
    }
  }
`;

function saunaVariables(fields: SaunaFields) {
  return {
    name: fields.name,
    description: fields.description || null,
    addressLine: fields.addressLine || null,
    city: fields.city || null,
    state: fields.state || null,
    postalCode: fields.postalCode || null,
    country: fields.country || null,
    latitude: fields.lat,
    longitude: fields.lng,
    capacity: parseInt(fields.capacity, 10),
    pricePerHourCents: parseInt(fields.pricePerHourCents, 10),
    saunaType: fields.saunaType || null,
    amenityIds: fields.amenityIds,
    requiresApproval: fields.requiresApproval,
  };
}

export default function SaunaCreateWizard() {
  const { userId } = useAuth();
  const { acknowledged: termsOk, loading: termsLoading } = useAcknowledgedTerms(userId);
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [fields, setFields] = useState<SaunaFields>(emptySaunaFields);
  const [saunaId, setSaunaId] = useState<number | null>(null);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const availabilityRef = useRef<AvailabilityEditorHandle>(null);

  const [createSauna, { loading: creating }] = useMutation(CREATE_SAUNA);
  const [updateSauna, { loading: updating }] = useMutation(UPDATE_SAUNA);
  const [setActive, { loading: publishing }] = useMutation(SET_ACTIVE, {
    refetchQueries: ["MySaunas"],
  });

  const handleInfoContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setInfoError(null);
    try {
      if (saunaId === null) {
        const { data, errors } = await createSauna({
          variables: { ...saunaVariables(fields), isActive: false },
        });
        if (errors) { setInfoError(errors[0]?.message ?? "Failed to create listing"); return; }
        const id = data?.createOwnedSauna?.sauna?.id;
        setSaunaId(id);
      } else {
        const { errors } = await updateSauna({
          variables: { id: saunaId, ...saunaVariables(fields) },
        });
        if (errors) { setInfoError(errors[0]?.message ?? "Failed to save"); return; }
      }
      setStep(1);
    } catch (err: any) {
      setInfoError(err?.message ?? "Something went wrong");
    }
  };

  const handlePublish = async () => {
    if (!saunaId) return;
    setPublishError(null);
    try {
      await availabilityRef.current?.save();
      const { errors } = await setActive({ variables: { id: saunaId, isActive: true } });
      if (errors) { setPublishError(errors[0]?.message ?? "Failed to publish"); return; }
      setStep(3);
    } catch (err: any) {
      setPublishError(err?.message ?? "Something went wrong");
    }
  };

  if (!termsLoading && !termsOk) {
    return (
      <Page>
        <h2 style={titleStyle}>Terms required</h2>
        <p style={{ color: "#374151", textAlign: "center", lineHeight: 1.6, marginBottom: 24, maxWidth: 440, margin: "0 auto 24px" }}>
          You must accept our terms of service before listing a sauna.
          Accidents, serious injury, or even death may occur. This application accepts
          complete and utter zero responsibility for any harm arising from sauna use.
        </p>
        <div style={{ textAlign: "center" }}>
          <Link
            to="/dashboard"
            style={{ display: "inline-block", padding: "10px 24px", background: "#2563EB", color: "#fff", borderRadius: 6, textDecoration: "none", fontSize: 14, fontWeight: 600 }}
          >
            Go to Dashboard to accept terms
          </Link>
        </div>
      </Page>
    );
  }

  if (step === 3) {
    return (
      <Page>
        <h2 style={titleStyle}>Your sauna is live</h2>
        <p style={{ color: "#6b7280", marginBottom: 28, textAlign: "center", lineHeight: 1.5 }}>
          Guests can now find and book it.
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <Link to={`/saunas/${saunaId}`} style={primaryLinkBtn}>View listing</Link>
          <button onClick={() => navigate("/dashboard")} style={secondaryBtn}>Dashboard</button>
        </div>
      </Page>
    );
  }

  return (
    <Page wide={step === 0}>
      <ProgressStepper step={step} />

      {step === 0 && (
        <SaunaForm
          fields={fields}
          onChange={setFields}
          onSubmit={handleInfoContinue}
          loading={creating || updating}
          error={infoError}
          submitLabel="Continue →"
        />
      )}

      {step === 1 && saunaId !== null && (
        <>
          <SaunaImageManager saunaId={saunaId} />
          <WizardFooter
            onBack={() => setStep(0)}
            onContinue={() => setStep(2)}
            continueLabel="Continue →"
          />
        </>
      )}

      {step === 2 && saunaId !== null && (
        <>
          <h3 style={{ margin: "0 0 16px", fontSize: 16 }}>Availability</h3>
          <AvailabilityEditor ref={availabilityRef} saunaId={saunaId} hideSaveButton />
          {publishError && (
            <p style={{ color: "#ef4444", fontSize: 13, marginTop: 8 }}>{publishError}</p>
          )}
          <WizardFooter
            onBack={() => setStep(1)}
            onContinue={handlePublish}
            continueLabel={publishing ? "Publishing…" : "Publish listing"}
            loading={publishing}
          />
        </>
      )}
    </Page>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Page({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <div style={pageStyle}>
      <div style={{ ...cardStyle, maxWidth: wide ? 640 : 560 }}>{children}</div>
    </div>
  );
}

function ProgressStepper({ step }: { step: number }) {
  const labels = ["Info", "Photos", "Schedule"];
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "center", marginBottom: 32 }}>
      {labels.map((label, i) => (
        <React.Fragment key={i}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 14,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 13, fontWeight: 600,
              background: i <= step ? "#2563eb" : "#e5e7eb",
              color: i <= step ? "#fff" : "#9ca3af",
              transition: "background 200ms",
            }}>
              {i + 1}
            </div>
            <span style={{
              fontSize: 11, fontWeight: i === step ? 600 : 400,
              color: i <= step ? "#1d4ed8" : "#9ca3af",
            }}>
              {label}
            </span>
          </div>
          {i < labels.length - 1 && (
            <div style={{
              height: 2, width: 56, marginTop: 13,
              background: i < step ? "#2563eb" : "#e5e7eb",
              transition: "background 200ms",
            }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function WizardFooter({ onBack, onContinue, continueLabel, loading }: {
  onBack: () => void;
  onContinue: () => void;
  continueLabel: string;
  loading?: boolean;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 28 }}>
      <button type="button" onClick={onBack} style={backBtn}>← Back</button>
      <button type="button" onClick={onContinue} disabled={loading} style={primaryBtn}>
        {continueLabel}
      </button>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  minHeight: "calc(100vh - 56px)",
  display: "flex", alignItems: "flex-start", justifyContent: "center",
  padding: "60px 24px 80px",
  background: "#fafafa",
};

const cardStyle: React.CSSProperties = {
  width: "100%",
  background: "#fff", borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: "32px 36px 28px",
};

const titleStyle: React.CSSProperties = {
  fontSize: 22, fontWeight: 600, margin: "8px 0 12px", textAlign: "center",
};

const primaryBtn: React.CSSProperties = {
  padding: "10px 24px", background: "#2563eb",
  color: "#fff", border: "none", borderRadius: 6,
  cursor: "pointer", fontSize: 14, fontWeight: 600,
};

const primaryLinkBtn: React.CSSProperties = {
  ...primaryBtn,
  display: "inline-block",
  textDecoration: "none",
};

const secondaryBtn: React.CSSProperties = {
  padding: "10px 24px", background: "#fff",
  color: "#374151", border: "1px solid #d1d5db", borderRadius: 6,
  cursor: "pointer", fontSize: 14, fontWeight: 500,
};

const backBtn: React.CSSProperties = {
  background: "none", border: "none", cursor: "pointer",
  color: "#6b7280", fontSize: 13, padding: 0,
};
